import { NgTemplateOutlet } from '@angular/common';
import {
    Component,
    computed,
    DestroyRef,
    Injector,
    inject,
    input,
    type Signal,
    signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ButtonModule } from '@openng/optimus-ui/button';
import { MessageModule } from '@openng/optimus-ui/message';
import { PopoverModule } from '@openng/optimus-ui/popover';
import { SelectModule } from '@openng/optimus-ui/select';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { isNil } from 'lodash-es';
import { injectParams } from 'ngxtension/inject-params';
import type { Observable } from 'rxjs';

import type { FoundationModelModel } from '@app/modules/assets/models/foundation-model.model';
import type { GlobalFunctionModel } from '@app/modules/assets/models/global-function.model';
import {
    PIPELINE_TYPE,
    PIPELINE_TYPE_INFO,
} from '@app/modules/assets/models/pipeline-builder.model';
import type { PromptModel } from '@app/modules/assets/models/prompt.model';
import type { RagModel } from '@app/modules/assets/models/rag.model';
import type { FoundationModelService } from '@app/modules/assets/services/foundation-model.service';
import type { PipelineService } from '@app/modules/assets/services/pipeline-builder.service';
import type { PromptService } from '@app/modules/assets/services/prompt.service';
import type { RagService } from '@app/modules/assets/services/rag.service';
import { DialogBoxComponent } from '@app/shared/components/dialog-box/dialog-box.component';
import { InputSelectComponent } from '@app/shared/components/input-select/input-select.component';
import {
    EXECUTION_ICON,
    EXECUTION_INFO,
    EXECUTION_TYPE,
    GGX_GLOBAL_OBJECTS,
    INPUT_CASES,
    OBJECT_DISPLAY_NAMES,
    OBJECT_INFO,
    OBJECTS,
    SCHEDULED_JOB_STATUS,
    SIMULATABLE_OBJECTS,
    type WORKFLOW_STATUS,
} from '@app/shared/constants/shared.constant';
import { FindItemPipe } from '@app/shared/pipes/find-item.pipe';
import type { ListResponse } from '@app/shared/services/mixins/crud.service';
import { OBJECT_SERVICE_MAPPER } from '@app/shared/utils/object-service-mapper';
import { RouterHelper } from '@app/shared/utils/router-helper';

interface ExecutionOption {
    link: string;
    linkQueryParams?:
        | {
              // For run-job page
              type: EXECUTION_TYPE;
              entityId: number;
              object: OBJECTS;
          }
        | { chatSessionRunId: number }; // For chat session in pipeline
    visibility: 'disabled' | 'visible' | 'hidden';
    name: string;
    tooltip: string;
    type: EXECUTION_TYPE | 'singleRun';
    icon: string;
    action?: () => void;
}

/**
 * Execution Options is a component which provide the Run button-dropdown to use in the object and
 * sub-object (for transform) pages
 */
@Component({
    selector: 'cor-execution-options',
    templateUrl: './execution-options.component.html',
    imports: [
        TooltipModule,
        RouterModule,
        ButtonModule,
        MessageModule,
        PopoverModule,
        DialogBoxComponent,
        InputSelectComponent,
        FormsModule,
        ReactiveFormsModule,
        SelectModule,
        FindItemPipe,
        NgTemplateOutlet,
    ],
})
export class ExecutionOptionsComponent {
    readonly currentProject = injectParams<string>('projectSlug');
    /**
     * Specifies the id of the entity using it, e.g. model_version_id, feature_version_id
     */
    readonly id = input.required<number>();

    /**
     * Holds which object we are showing execution options for
     */
    readonly object = input.required<OBJECTS>();

    /**
     * The item whose execution options are being rendered - ONLY relevant for Pipeline/Report so we can
     * check if it is a chat based object or not to disable/enable the Chat option in Run.
     */
    readonly item = input<{
        id: number;
        currentStatus?: WORKFLOW_STATUS;
        pipelineType?: PIPELINE_TYPE; // Only used for pipeline
        objectTypes?: OBJECTS[]; // Only used for report
    }>();

    protected comparison = {
        showDialog: signal(false),
        step: signal<'options' | 'noComparisonJobs' | 'selectComparisonJobs'>('options'),
        leftJobId: signal<number>(null),
        rightJobId: signal<number>(null),
        link: computed(
            (): string =>
                `${RouterHelper.buildRoute({ object: this.object(), id: this.id(), projectSlug: this.currentProject() })}/simulations/${this.comparison.leftJobId()}`,
        ),
    };

    protected hillClimbing = {
        showDialog: signal(false),
        step: signal<'options' | 'noBaseJob' | 'selectBaseJob'>('options'),
        baseJobId: signal<number>(null),
        link: computed(
            () =>
                `${RouterHelper.buildRoute({ object: this.object(), id: this.id(), projectSlug: this.currentProject() })}/run-job`,
        ),
    };

    protected reportExecution = {
        showDialog: signal(false),
        objects: computed(() =>
            SIMULATABLE_OBJECTS.map(x => {
                const disabled = !this.item()?.objectTypes.includes(x);
                return {
                    id: x,
                    name:
                        OBJECT_DISPLAY_NAMES[x] +
                        (disabled ? ' (Not supported for this report)' : ''),
                    icon: OBJECT_INFO[x]?.icon,
                    disabled,
                };
            }),
        ),
        objectItemsList: signal<{ id: number; name: string }[]>([]),
        loadingItems: signal(false),
        selectedObject: signal<OBJECTS>(null),
        selectedItem: signal<number>(null),
        link: computed(
            () =>
                `${RouterHelper.buildRoute({ object: this.object(), id: this.id(), projectSlug: this.currentProject() })}/run-job`,
        ),
    };

    protected readonly reportExecutionlink = computed(() => {
        if (!this.reportExecution.selectedObject() || !this.reportExecution.selectedItem()) return;
        return `${RouterHelper.buildRoute({ object: this.reportExecution.selectedObject(), id: this.reportExecution.selectedItem(), projectSlug: this.currentProject() })}/run-job`;
    });

    protected readonly service: Signal<
        FoundationModelService | PromptService | RagService | PipelineService
    > = computed(() =>
        this.injector.get<any>(OBJECT_SERVICE_MAPPER[this.reportExecution.selectedObject()]),
    );

    private readonly injector = inject(Injector);
    private readonly destroyRef = inject(DestroyRef);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);

    protected readonly INPUT_CASES = INPUT_CASES;
    protected readonly SCHEDULED_JOB_STATUS = SCHEDULED_JOB_STATUS;
    protected readonly EXECUTION_TYPE = EXECUTION_TYPE;
    protected readonly OBJECT_DISPLAY_NAMES = OBJECT_DISPLAY_NAMES;

    protected readonly executionOptions = computed<ExecutionOption[]>(() => {
        const executionOptions: {
            name: EXECUTION_TYPE;
            disabled?: boolean; // Defaults to true
            tooltip?: string;
        }[] = [];

        if (this.object() === OBJECTS.MONITORING) {
            executionOptions.push({ name: EXECUTION_TYPE.MONITORING });
        } else if (this.object() === OBJECTS.REPORT) {
            executionOptions.push({ name: EXECUTION_TYPE.SIMULATION });
        } else if (
            [OBJECTS.FOUNDATION_MODEL, OBJECTS.RAG, OBJECTS.GLOBAL_FUNCTION].includes(this.object())
        ) {
            const item = this.item() as GlobalFunctionModel | RagModel | FoundationModelModel;
            if (isNil(item.type)) {
                executionOptions.push({
                    name: EXECUTION_TYPE.SIMULATION,
                    disabled: true,
                    tooltip: `Not supported as Output Type is not specified`,
                });
                executionOptions.push({
                    name: EXECUTION_TYPE.COMPARISON,
                    disabled: true,
                    tooltip: `Not supported as Output Type is not specified`,
                });
            } else if (item.arguments.some(arg => isNil(arg.inputType))) {
                executionOptions.push({
                    name: EXECUTION_TYPE.SIMULATION,
                    disabled: true,
                    tooltip: `Not supported as some argument types are not specified`,
                });
                executionOptions.push({
                    name: EXECUTION_TYPE.COMPARISON,
                    disabled: true,
                    tooltip: `Not supported as some argument types are not specified`,
                });
            } else {
                executionOptions.push({ name: EXECUTION_TYPE.SIMULATION });
            }
        } else if (this.object() === OBJECTS.PROMPT) {
            const prompt = this.item() as PromptModel;
            if (prompt.arguments.some(arg => isNil(arg.inputType))) {
                executionOptions.push({
                    name: EXECUTION_TYPE.SIMULATION,
                    disabled: true,
                    tooltip: `Not supported as some argument types are not specified`,
                });
                executionOptions.push({
                    name: EXECUTION_TYPE.COMPARISON,
                    disabled: true,
                    tooltip: `Not supported as some argument types are not specified`,
                });
            } else {
                executionOptions.push({ name: EXECUTION_TYPE.SIMULATION });
                executionOptions.push({ name: EXECUTION_TYPE.COMPARISON });
            }
        } else if (this.object() === OBJECTS.PIPELINE) {
            executionOptions.push({ name: EXECUTION_TYPE.SIMULATION });
            executionOptions.push({ name: EXECUTION_TYPE.HILL_CLIMBING });
            executionOptions.push({ name: EXECUTION_TYPE.COMPARISON });
        } else if (GGX_GLOBAL_OBJECTS.includes(this.object())) {
            // Other (non-pipeline) GenAI objects
            executionOptions.push({ name: EXECUTION_TYPE.SIMULATION });
            executionOptions.push({ name: EXECUTION_TYPE.COMPARISON });
        } else {
            // Should never occur - but just to be safe. Show simulation only
            executionOptions.push({ name: EXECUTION_TYPE.SIMULATION });
        }

        const options: ExecutionOption[] = executionOptions.map(x => {
            const option: ExecutionOption = {
                type: x.name,
                ...EXECUTION_INFO[x.name],
                link: `${RouterHelper.buildRoute({ object: this.object(), id: this.id(), projectSlug: this.currentProject() })}/run-job`,
                linkQueryParams: {
                    type: x.name,
                    entityId: this.id(),
                    object: this.object(),
                },
                visibility: x.disabled ? 'disabled' : 'visible',
                tooltip: x.tooltip || EXECUTION_INFO[x.name].tooltip,
                icon: EXECUTION_ICON[x.name],
            };
            // For Hill Climbing, return option with an action and no routing links
            if (x.name === EXECUTION_TYPE.HILL_CLIMBING) {
                return {
                    ...option,
                    link: undefined,
                    linkQueryParams: undefined,
                    action: () => this.hillClimbing.showDialog.set(true),
                };
            }
            // For Comparison, return option with an action and no routing links
            if (x.name === EXECUTION_TYPE.COMPARISON) {
                return {
                    ...option,
                    link: undefined,
                    linkQueryParams: undefined,
                    action: () => this.comparison.showDialog.set(true),
                };
            }

            if (this.object() === OBJECTS.REPORT) {
                return {
                    ...option,
                    link: undefined,
                    linkQueryParams: undefined,
                    action: () => this.reportExecution.showDialog.set(true),
                };
            }

            return option;
        });
        if (this.object() === OBJECTS.PIPELINE) {
            const pipelineType = this.item().pipelineType;
            if (!pipelineType) return options;

            const pipelineTypeInfo = PIPELINE_TYPE_INFO[pipelineType];
            const supportsSingleRuns = [PIPELINE_TYPE.CHAT, PIPELINE_TYPE.SUMMARIZATION].includes(
                pipelineType,
            );
            options.push({
                type: 'singleRun',
                name: pipelineTypeInfo.singleRun,
                tooltip: supportsSingleRuns
                    ? 'Run this pipeline with a single input'
                    : `Not available for ${pipelineType} pipelines`,
                link: undefined,
                linkQueryParams: undefined,
                visibility: supportsSingleRuns ? 'visible' : 'disabled',
                icon: pipelineTypeInfo.icon,
                action: () => {
                    this.router.navigate([], {
                        relativeTo: this.route,
                        queryParams: { chatSessionRunId: -1 },
                        queryParamsHandling: 'merge',
                    });
                },
            });
        }

        return options;
    });

    protected readonly executionOptionCategories = computed(() => [
        {
            name: 'Batch Evals',
            options: this.executionOptions().filter(option => option.type !== 'singleRun'),
        },
        {
            name: 'Single Evals',
            options: this.executionOptions().filter(option => option.type === 'singleRun'),
        },
    ]);

    // If there is only 1 visible option - we set it here, so we can avoid 2 clicks on it
    protected readonly singleOption = computed(() =>
        this.executionOptions().length === 1 ? this.executionOptions()[0] : undefined,
    );

    constructor() {
        toObservable(this.item)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(item => {
                if (item?.objectTypes?.length === 1) {
                    this.reportExecution.selectedObject.set(item.objectTypes[0]);
                    this.onObjectChange(this.reportExecution.selectedObject());
                }
            });
    }

    protected onCloseHillClimbingDialog() {
        this.hillClimbing.showDialog.set(false);
        this.hillClimbing.step.set('options');
        this.hillClimbing.baseJobId.set(null);
    }

    protected onCloseComparisonDialog() {
        this.comparison.showDialog.set(false);
        this.comparison.step.set('options');
        this.comparison.leftJobId.set(null);
        this.comparison.rightJobId.set(null);
    }

    protected onCloseReportExecutionDialog() {
        this.reportExecution.showDialog.set(false);
        this.reportExecution.selectedObject.set(null);
        this.reportExecution.selectedItem.set(null);
    }

    protected onObjectChange(object: OBJECTS) {
        if (!object) return;
        this.reportExecution.selectedItem.set(null);
        this.reportExecution.loadingItems.set(true);
        (
            this.service().getAll({ simple: true }) as Observable<
                ListResponse<{ id: number; name: string }>
            >
        )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({ result }) => {
                this.reportExecution.objectItemsList.set(result);
            })
            .add(() => this.reportExecution.loadingItems.set(false));
    }
}
